import React, { useEffect, useState } from 'react';
import { Truck, Package, CheckCircle, TrendingUp, X } from 'lucide-react';
import api from '../api';
import StatTile from '../components/StatTile';

const C = { blue:'#0284c7', green:'#16a34a', red:'#dc2626', amber:'#f59e0b', purple:'#7c3aed', slate:'#64748b' };
const money = n => 'AED ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const mins = n => { const v = Math.round(Number(n || 0)); if (!v) return '0m'; const h = Math.floor(v/60), m = v%60; return h ? `${h}h ${m}m` : `${m}m`; };
const shortDate = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric' });
const card = { background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' };
const th = { padding:'10px 14px', textAlign:'left', color:'#64748b', fontWeight:500, fontSize:12 };
const td = { padding:'11px 14px', color:'#475569', fontSize:13 };

const StatCard = StatTile;

const Empty = ({ text }) => <div style={{ padding:28, textAlign:'center', color:'#94a3b8', fontSize:13 }}>{text}</div>;

export default function Dashboard() {
  const todayIso = new Date().toISOString().split('T')[0];

  const [todayData, setTodayData] = useState(null);

  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    api.get('/analytics/dashboard?date=' + todayIso).then(r => setTodayData(r.data)).catch(() => {});
  }, [todayIso]);

  const openDrill = async (opts) => {
    setDrill(opts); setDrillData(null); setDrillLoading(true);
    const p = new URLSearchParams();
    if (opts.date) p.set('date', opts.date);
    if (opts.driver_id) p.set('driver_id', opts.driver_id);
    if (opts.status) p.set('status', opts.status);
    try {
      const { data } = await api.get('/analytics/invoices-detail?' + p.toString());
      setDrillData(data);
    } catch (e) { setDrillData({ count:0, total_value:0, invoices:[] }); }
    finally { setDrillLoading(false); }
  };

  const t = todayData?.totals || {};

  return (
    <div style={{ padding:24 }}>
      <div style={{ marginBottom:18 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:'#1e293b', margin:0 }}>Dashboard</h1>
        <p style={{ color:'#64748b', fontSize:13, margin:'4px 0 0' }}>
          {new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
        </p>
      </div>

      <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap' }}>
        <StatCard icon={Truck} label="Active drivers" value={t.total_sessions || 0} color={C.blue} sub="Today" />
        <StatCard icon={Package} label="Total invoices" value={t.total_invoices || 0} color={C.purple} sub="Today" />
        <StatCard icon={CheckCircle} label="Delivered" value={t.delivered || 0} color={C.green} sub="Completed" />
        <StatCard icon={TrendingUp} label="Invoice value" value={money(t.total_value)} color="#ea580c" sub="Today total" />
      </div>

      <div style={{ ...card, overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9' }}>
          <h2 style={{ fontSize:15, fontWeight:600, color:'#1e293b', margin:0 }}>Driver status — today</h2>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>Click a row to see that driver's stops</div>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'#f8fafc' }}>
            {['Driver','Status','Stops','Completed','Invoice Value','Distance'].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(todayData?.sessions || []).map(s => (
              <tr key={s.id} style={{ borderTop:'1px solid #f1f5f9', cursor:'pointer' }}
                onClick={() => openDrill({ date:todayIso, driver_id:s.driver_id, title:`${s.driver_name} — today` })}>
                <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{s.driver_name}</td>
                <td style={td}>
                  <span style={{ padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:500,
                    background: s.status==='completed' ? '#dcfce7' : s.status==='in_progress' ? '#dbeafe' : '#fef9c3',
                    color: s.status==='completed' ? C.green : s.status==='in_progress' ? '#1d4ed8' : '#854d0e' }}>
                    {s.status.replace('_',' ')}</span>
                </td>
                <td style={td}>{s.total_stops}</td>
                <td style={{ ...td, color:C.green, fontWeight:500 }}>{s.completed_stops}</td>
                <td style={{ ...td, color:'#1e293b' }}>{money(s.total_value)}</td>
                <td style={td}>{s.total_distance || 0} km</td>
              </tr>
            ))}
            {!todayData?.sessions?.length && <tr><td colSpan={6}><Empty text="No deliveries today" /></td></tr>}
          </tbody>
        </table>
      </div>

      {drill && (
        <div onClick={() => setDrill(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:1000, maxHeight:'88vh', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ fontSize:16, fontWeight:700, color:'#1e293b', margin:0, textTransform:'capitalize' }}>{drill.title || 'Details'}</h2>
                {drillData && (
                  <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>
                    {drillData.count} invoices · {money(drillData.total_value)}
                    {drillData.count >= 500 && ' (showing first 500)'}
                  </div>
                )}
              </div>
              <button onClick={() => setDrill(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b' }}><X size={20} /></button>
            </div>
            <div style={{ overflow:'auto', padding:'0 4px 4px' }}>
              {drillLoading && <Empty text="Loading..." />}
              {drillData && !drillData.invoices.length && <Empty text="No invoices match this selection" />}
              {drillData && drillData.invoices.length > 0 && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'#f8fafc', position:'sticky', top:0 }}>
                    {['Date','Invoice','Customer','Driver','Value','Status','Detail'].map(h => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {drillData.invoices.map(inv => {
                      const st = inv.delivery_status === 'cancelled' ? 'cancelled'
                        : inv.delivery_status === 'rescheduled' ? 'rescheduled'
                        : inv.status === 'delivered' ? 'delivered' : 'pending';
                      const tint = st==='delivered' ? C.green : st==='cancelled' ? C.red : st==='rescheduled' ? C.purple : '#94a3b8';
                      const bg = st==='delivered' ? '#dcfce7' : st==='cancelled' ? '#fee2e2' : st==='rescheduled' ? '#f3e8ff' : '#f1f5f9';
                      return (
                        <tr key={inv.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                          <td style={{ ...td, fontSize:12 }}>{shortDate(inv.session_date)}</td>
                          <td style={{ ...td, fontSize:12, fontWeight:600, color:'#1e293b' }}>{inv.invoice_no || '—'}</td>
                          <td style={{ ...td, color:'#1e293b' }}>
                            {inv.customer_name}
                            <div style={{ fontSize:11, color:'#94a3b8' }}>{inv.customer_code} · {inv.salesman_name}</div>
                          </td>
                          <td style={{ ...td, fontSize:12 }}>{inv.driver_name}</td>
                          <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{money(inv.invoice_value)}</td>
                          <td style={td}>
                            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, background:bg, color:tint, textTransform:'capitalize' }}>{st}</span>
                          </td>
                          <td style={{ ...td, fontSize:11, color:'#64748b' }}>
                            {inv.cancel_reason && <div style={{ color:C.red }}>{inv.cancel_reason}</div>}
                            {inv.reschedule_date && <div style={{ color:C.purple }}>To {shortDate(inv.reschedule_date)}</div>}
                            {(inv.travel_time != null || inv.service_time != null) && (
                              <div>Travel {mins(inv.travel_time)} · Shop {mins(inv.service_time)}</div>
                            )}
                            {inv.location_distance != null && (
                              <div style={{ color: inv.location_distance > 500 ? C.red : '#94a3b8' }}>
                                {inv.location_distance >= 1000 ? (inv.location_distance/1000).toFixed(1)+' km' : inv.location_distance+' m'} off
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
